/**
 * Componente de Alerta de Saúde da DLQ
 * 
 * Exibe alertas baseados na quantidade de itens na DLQ
 * e no tempo dos itens mais antigos.
 * 
 * @author Sistema de Flows v3.0 - DLQ
 */

import { AlertTriangle, CheckCircle2, Skull } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { useDlqStats } from '@/hooks/useDlqMonitor';
import { formatDistanceToNow, differenceInHours } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function DlqHealthAlert() {
  const { data: stats, isLoading } = useDlqStats();

  if (isLoading) {
    return <Skeleton className="h-16 w-full" />;
  }

  const total = stats?.total || 0;
  const oldestAt = stats?.oldestAt ? new Date(stats.oldestAt) : null;
  const hoursOld = oldestAt ? differenceInHours(new Date(), oldestAt) : 0;

  // Se não há itens na DLQ, mostra status saudável
  if (total === 0) {
    return (
      <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
        <CheckCircle2 className="h-4 w-4 text-green-600" />
        <AlertTitle className="text-green-800 dark:text-green-200">
          DLQ vazia
        </AlertTitle>
        <AlertDescription className="text-green-700 dark:text-green-300">
          Nenhuma mensagem falhada aguardando ação. O sistema está operando normalmente.
        </AlertDescription>
      </Alert>
    );
  }

  // Itens na DLQ por muito tempo (>24h)
  if (hoursOld > 24) {
    return (
      <Alert variant="destructive">
        <Skull className="h-4 w-4" />
        <AlertTitle>Atenção: Itens antigos na DLQ</AlertTitle>
        <AlertDescription>
          {total} {total === 1 ? 'item' : 'itens'} na DLQ, o mais antigo há{' '}
          {oldestAt && formatDistanceToNow(oldestAt, { locale: ptBR })}.
          Revise e reprocesse ou descarte os itens pendentes.
        </AlertDescription>
      </Alert>
    );
  }

  // Muitos itens na DLQ (>10)
  if (total > 10) {
    return (
      <Alert className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertTitle className="text-yellow-800 dark:text-yellow-200">
          DLQ com {total} itens
        </AlertTitle>
        <AlertDescription className="text-yellow-700 dark:text-yellow-300">
          Há {total} mensagens aguardando ação na Dead Letter Queue.
          Verifique os motivos de falha e tome as ações necessárias.
        </AlertDescription>
      </Alert>
    );
  }

  // Poucos itens na DLQ (1-10)
  return (
    <Alert className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950">
      <AlertTriangle className="h-4 w-4 text-orange-600" />
      <AlertTitle className="text-orange-800 dark:text-orange-200">
        {total} {total === 1 ? 'item' : 'itens'} na DLQ
      </AlertTitle>
      <AlertDescription className="text-orange-700 dark:text-orange-300">
        Mensagens falhadas aguardando revisão. Verifique os motivos e decida se devem ser reprocessadas ou descartadas.
      </AlertDescription>
    </Alert>
  );
}
